import { useEffect, useMemo, useState } from 'react';
import { parseConsignmentOrderDetailsForModal, parseBulkConsignmentOrderDetailsForModal } from '@/components/Templates/ConsignmentOrders/utils';
import {
	parseBulkOrderDetailsForModal,
	parseOrderDetailsForModal,
} from '@/components/Templates/Requests/utils';
import { useGetSingleBV } from '@/hooks/service-hooks/benefitsVerification';
import { BgProcessModule } from '@/hooks/service-hooks/bgProcess';
import { useGetConsignmentByIdMutation } from '@/hooks/service-hooks/consignmentOrders';
import { useGetSingleDenialSupport } from '@/hooks/service-hooks/denialSupport';
import { useGetPatient } from '@/hooks/service-hooks/patients';
import { useGetOrderByIdMutation } from '@/hooks/service-hooks/requests';
import { useGetSupportTicket } from '@/hooks/service-hooks/supportTickets';
import { getOrderRequestById as getOrderByIdService } from '@/services/requests';
import { getConsignmentOrderById as getConsignmentByIdService } from '@/services/consignmentOrders';
import { parsePatientDataForDetailsModal } from '@/utils/detailsModalUtils';
import { transformBVRecordToPreview } from './utils';

export const useBgModuleDetails = (
	moduleName?: BgProcessModule,
	payload?: any,
) => {
	
	// Apis calls for BVs
	const {
		mutate: getBVDetails,
		isLoading: isBVLoading,
		data: bvDetails,
	} = useGetSingleBV();

	const clinicId = bvDetails?._ta_clinic_value ?? '';
	const accountProducts = useMemo(() => {
		if (!bvDetails?.priorAuths) return [];
		return (
			bvDetails?.priorAuths?.map((p) => ({
					label: p.ta_name,
					value: p.ta_pa_status,
					qCode: p.ta_priorauthid,
				})) ?? []
		);
	}, [bvDetails, clinicId]);

	//Apis calls for Order Requests Single
	const {
		mutate: getOrderRequestById,
		data: orderDetails,
		isLoading: isOrderDetailsLoading,
	} = useGetOrderByIdMutation();

	//Api calls for Consignment Orders
	const {
		mutate: getConsignmentOrderById,
		data: consignmentOrderDetails,
		isLoading: isConsignmentLoading,
	} = useGetConsignmentByIdMutation();

	//Api call for Patients
	const { data: patientDetails, isFetching: isPatientDetailsLoading } =
		useGetPatient(
			payload?.response?.body?.contactid || null,
			moduleName === BgProcessModule.PATIENTS,
		);

	//Api clls for Support Tickets
	const { data: ticketDetails, isFetching: isTicketDetailsLoading } =
		useGetSupportTicket(
			payload?.response?.body?.incidentid || null,
			moduleName === BgProcessModule.SUPPORT_TICKET,
		);

	//Api calls for Denial Support
	const { data: denialSupportDetails, isFetching: isDenialSupportDetailsLoading } =
		useGetSingleDenialSupport(
			payload?.response?.body?.ta_denialsid || null,
			moduleName === BgProcessModule.DENIAL_SUPPORT,
		);

	// Bulk orders: fetch all CRM orders by ID so we get full ta_SKU details
	const [bulkOrdersDetails, setBulkOrdersDetails] = useState<any[] | null>(null);
	const [isBulkDetailsLoading, setIsBulkDetailsLoading] = useState(false);

	// Bulk consignment orders: fetch all CRM consignment orders by ID so we get full ta_SKU details
	const [bulkConsignmentDetails, setBulkConsignmentDetails] = useState<any[] | null>(null);
	const [isBulkConsignmentLoading, setIsBulkConsignmentLoading] = useState(false);

	useEffect(() => {
		if (moduleName !== BgProcessModule.ORDER_REQUEST_BULK) {
			setBulkOrdersDetails(null);
			setIsBulkDetailsLoading(false);
		}
		if (moduleName !== BgProcessModule.CONSIGNMENT_ORDER) {
			setBulkConsignmentDetails(null);
			setIsBulkConsignmentLoading(false);
		}
		switch (moduleName) {
			case BgProcessModule.BV: {
				const bvId = payload?.response?.body?.ta_benefitsverificationid ?? '';
				bvId && getBVDetails(bvId);
				break;
			}
			case BgProcessModule.ORDER_REQUEST_SINGLE: {
				const orderId = payload?.response?.body?.ta_orderrequestid ?? '';
				orderId && getOrderRequestById(orderId);
				break;
			}
		case BgProcessModule.ORDER_REQUEST_BULK: {
			// Results can be in response.body.results (cron) or response.data.results (Orders API shape)
			const bodyOrData = payload?.response?.body ?? payload?.response?.data;
			const results = bodyOrData?.results ?? [];
			
			// Extract order IDs: each result may have orderId, or data.ta_orderrequestid, or customData.orderIds (stored by cron)
				let orderIds: string[] = results
					.map(
						(r: {
							orderId?: string;
							data?: { ta_orderrequestid?: string };
							ta_orderrequestid?: string;
						}) =>
							r?.orderId ??
							r?.data?.ta_orderrequestid ??
							r?.ta_orderrequestid,
					)
					.filter(Boolean);
				if (orderIds.length === 0 && payload?.customData?.orderIds) {
					try {
						const stored = payload.customData.orderIds;
						orderIds =
							typeof stored === 'string' ? JSON.parse(stored) : stored ?? [];
						orderIds = Array.isArray(orderIds) ? orderIds.filter(Boolean) : [];
					} catch {
				orderIds = [];
			}
		}
		
		if (orderIds.length > 0) {
					setIsBulkDetailsLoading(true);
					setBulkOrdersDetails(null);
				Promise.all(
					orderIds.map((id: string) => 
						Promise.resolve(getOrderByIdService(id))
							.then(res => res)
							.catch(() => null)
					),
				)
					.then((responses) => {
						// Orders API returns { data: order } or { data: order, status, message }
						const orders = responses
							.map((r: any) => r?.data ?? r)
							.filter(Boolean);
						
						setBulkOrdersDetails(orders);
						})
						.catch(() => setBulkOrdersDetails([]))
						.finally(() => setIsBulkDetailsLoading(false));
				} else {
					setBulkOrdersDetails(null);
					setIsBulkDetailsLoading(false);
				}
				break;
			}
		case BgProcessModule.CONSIGNMENT_ORDER: {
			// Results can be in response.body.results (cron) or response.data.results (Orders API shape)
			const bodyOrData = payload?.response?.body ?? payload?.response?.data;
			const results = bodyOrData?.results ?? [];
			
			// Extract order IDs: each result may have orderId, or data.ta_orderrequestid, or customData.orderIds (stored by cron)
			let orderIds: string[] = results
				.map(
					(r: {
						orderId?: string;
						data?: { ta_orderrequestid?: string };
						ta_orderrequestid?: string;
					}) =>
						r?.orderId ??
						r?.data?.ta_orderrequestid ??
						r?.ta_orderrequestid,
				)
				.filter(Boolean);
			if (orderIds.length === 0 && payload?.customData?.orderIds) {
				try {
					const stored = payload.customData.orderIds;
					orderIds =
						typeof stored === 'string' ? JSON.parse(stored) : stored ?? [];
					orderIds = Array.isArray(orderIds) ? orderIds.filter(Boolean) : [];
				} catch {
					orderIds = [];
				}
			}
			
			if (orderIds.length > 0) {
				setIsBulkConsignmentLoading(true);
				setBulkConsignmentDetails(null);
				Promise.all(
					orderIds.map((id: string) => 
						Promise.resolve(getConsignmentByIdService(id))
							.then(res => res)
							.catch(() => null)
					),
				)
					.then((responses) => {
						// Orders API returns { data: order } or { data: order, status, message }
						const orders = responses
							.map((r: any) => r?.data ?? r)
							.filter(Boolean);
						
						setBulkConsignmentDetails(orders);
					})
					.catch(() => setBulkConsignmentDetails([]))
					.finally(() => setIsBulkConsignmentLoading(false));
			} else {
				setBulkConsignmentDetails(null);
				setIsBulkConsignmentLoading(false);
			}
			break;
		}
			default:
				break;
		}
	}, [moduleName, payload, getBVDetails]);

	switch (moduleName) {
		case BgProcessModule.BV:
			return {
				isLoading: isBVLoading,
				data: transformBVRecordToPreview(
					bvDetails || null,
					accountProducts || [],
				),
			};

		case BgProcessModule.ORDER_REQUEST_SINGLE:
			return {
				isLoading: isOrderDetailsLoading,
				data: isOrderDetailsLoading
					? null
					: orderDetails && parseOrderDetailsForModal(orderDetails?.data),
			};
	case BgProcessModule.ORDER_REQUEST_BULK: {
		const parsedData =
			bulkOrdersDetails && bulkOrdersDetails.length > 0
				? parseBulkOrderDetailsForModal(bulkOrdersDetails)
				: null;
		
		return {
				isLoading: isBulkDetailsLoading,
				data: parsedData,
			};
		}
	case BgProcessModule.CONSIGNMENT_ORDER: {
		const parsedData =
			bulkConsignmentDetails && bulkConsignmentDetails.length > 0
				? parseBulkConsignmentOrderDetailsForModal(bulkConsignmentDetails)
				: null;
		
		return {
			isLoading: isBulkConsignmentLoading,
			data: parsedData,
		};
	}
		case BgProcessModule.PATIENTS:
			return {
				isLoading: isPatientDetailsLoading,
				data: isPatientDetailsLoading
					? null
					: patientDetails && parsePatientDataForDetailsModal(patientDetails),
			};
		case BgProcessModule.SUPPORT_TICKET:
			return {
				isLoading: isTicketDetailsLoading,
				data: isTicketDetailsLoading ? null : ticketDetails || null,
			};
		case BgProcessModule.DENIAL_SUPPORT:
			return {
				isLoading: isDenialSupportDetailsLoading,
				data: isDenialSupportDetailsLoading ? null : denialSupportDetails || null,
			};

		default:
			return { isLoading: false, data: null };
	}
};
